import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../../ComponnentsUI/common/Checkbox';
import { Textarea } from '../../ComponnentsUI/common/Textarea';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { styles } from './primitives';
import { useReportData } from './lib/report-context';
import {
  LuLoader2,
  LuCheck,
  LuDownload,
  LuClipboardList,
  LuPrinter,
  LuShare2,
} from 'react-icons/lu';
import { ShareDialog } from './share-dialog';
import { apiClient } from '../../../helpers/apiHelper';
import { Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';

function BooleanPill({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
}) {
  const active = value === true;
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
        active
          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 ring-1 ring-blue-400/30'
          : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-600 text-slate-500 dark:text-gray-400 hover:border-slate-300 dark:hover:border-gray-500'
      }`}
    >
      <span
        className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          active
            ? 'border-blue-500 bg-blue-500'
            : 'border-slate-300 dark:border-gray-500'
        }`}
      >
        {active && (
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path
              d="M1.5 4L3 5.5L6.5 2"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

const footerActionButtonClass =
  'h-8 w-full justify-center gap-1.5 rounded-lg text-[12px] font-semibold leading-none whitespace-nowrap shadow-none';

export function ReportFooterPanel() {
  const { t, i18n } = useTranslation('Report');
  const {
    isSaving,
    studyInfo,
    studyReportFields,
    onUpdateFindings,
    reportDetailMode,
    onReportDetailModeChange,
  } = useReportData();
  const [isDownloading, setIsDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const r = studyReportFields;

  const runDownload = useCallback(
    async (
      path: string,
      filename: string,
      params?: Record<string, string | boolean>
    ) => {
      setIsDownloading(true);
      try {
        const response = await apiClient.downloadFile(path, { params });
        const blob: Blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        message.error(t('footer.pdfFailed'));
      } finally {
        setIsDownloading(false);
      }
    },
    [t]
  );

  const exportMenuItems: MenuProps['items'] = useMemo(() => {
    const sid = studyInfo?.studyId;
    if (!sid) {
      return [];
    }
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';
    return [
      {
        key: 'summary-full',
        label: t('footer.exportSummaryReportWithImages'),
        onClick: () =>
          void runDownload(
            `report-sharing/${sid}`,
            `report_${sid}_summary.pdf`,
            {
              include_images: true,
              template_variant: 'clinical_summary',
              locale,
            }
          ),
      },
      {
        key: 'summary-text',
        label: t('footer.exportSummaryReportNoImages'),
        onClick: () =>
          void runDownload(
            `report-sharing/${sid}`,
            `report_${sid}_summary_text.pdf`,
            {
              include_images: false,
              template_variant: 'clinical_summary',
              locale,
            }
          ),
      },
      {
        key: 'pdf-full',
        label: t('footer.exportFullReportWithImages'),
        onClick: () =>
          void runDownload(`report-sharing/${sid}`, `report_${sid}.pdf`, {
            include_images: true,
            locale,
          }),
      },
      {
        key: 'pdf-text',
        label: t('footer.exportFullReportNoImages'),
        onClick: () =>
          void runDownload(`report-sharing/${sid}`, `report_${sid}_text.pdf`, {
            include_images: false,
            locale,
          }),
      },
      { type: 'divider' },
      {
        key: 'dcm-pdf-full',
        label: t('footer.exportEncapsulatedPdfDicomWithImages'),
        onClick: () =>
          void runDownload(
            `study_report/export/encapsulated-pdf-dicom/${sid}`,
            `report_${sid}_encapsulated.dcm`,
            { include_images: true, locale }
          ),
      },
      {
        key: 'dcm-pdf-text',
        label: t('footer.exportEncapsulatedPdfDicomNoImages'),
        onClick: () =>
          void runDownload(
            `study_report/export/encapsulated-pdf-dicom/${sid}`,
            `report_${sid}_text_encapsulated.dcm`,
            { include_images: false, locale }
          ),
      },
    ];
  }, [
    studyInfo?.studyId,
    runDownload,
    t,
    i18n.language,
    i18n.resolvedLanguage,
  ]);

  return (
    <div className="flex flex-col gap-2 p-3 h-full">
      <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">
        {t('footer.actions')}
      </span>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] text-slate-500 dark:text-gray-400">
          {t('reportMode.label')}
        </span>
        <div
          className="grid grid-cols-2 gap-0.5 rounded-lg bg-slate-100 dark:bg-gray-900 p-0.5 ring-1 ring-slate-200 dark:ring-gray-700"
          aria-label={t('reportMode.label')}
        >
          {(['minimal', 'full'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onReportDetailModeChange?.(mode)}
              aria-pressed={reportDetailMode === mode}
              disabled={!onReportDetailModeChange}
              className={`flex h-7 min-w-0 items-center justify-center rounded-md px-1 text-[11px] font-bold leading-none transition-colors ${
                reportDetailMode === mode
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:bg-white hover:text-slate-700 disabled:opacity-60 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
              }`}
            >
              <span className="whitespace-nowrap">
                {t(
                  mode === 'minimal' ? 'reportMode.minimal' : 'reportMode.full'
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      <Button size="sm" className={footerActionButtonClass} disabled={isSaving}>
        {isSaving ? (
          <>
            <LuLoader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            <span className="truncate">{t('footer.saving')}</span>
          </>
        ) : (
          <>
            <LuCheck className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t('footer.saved')}</span>
          </>
        )}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        className={footerActionButtonClass}
        onClick={() => window.print()}
      >
        <LuPrinter className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{t('footer.print')}</span>
      </Button>
      <Dropdown
        menu={{ items: exportMenuItems }}
        trigger={['click']}
        disabled={isDownloading || !studyInfo?.studyId}
      >
        <Button
          size="sm"
          variant="secondary"
          className={footerActionButtonClass}
          disabled={isDownloading || !studyInfo?.studyId}
        >
          {isDownloading ? (
            <>
              <LuLoader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span className="truncate">{t('footer.generating')}</span>
            </>
          ) : (
            <>
              <LuDownload className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t('footer.exportPdf')}</span>
            </>
          )}
        </Button>
      </Dropdown>

      {/* Sharing with the patient is a separate act from exporting for the
          record, so it gets its own control rather than an export menu row. */}
      <Button
        size="sm"
        variant="secondary"
        className={footerActionButtonClass}
        disabled={!studyInfo?.studyId}
        onClick={() => setShareOpen(true)}
        title={t('share.title')}
      >
        <LuShare2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{t('share.shareWithPatient')}</span>
      </Button>

      {studyInfo?.studyId && (
        <ShareDialog
          open={shareOpen}
          studyId={studyInfo.studyId}
          onClose={() => setShareOpen(false)}
        />
      )}

      <div className="border-t border-slate-200 dark:border-gray-700 my-1" />

      <div className="flex items-center gap-1.5">
        <Checkbox
          id="mdt"
          defaultChecked={r.refer_to_mdt === true}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onUpdateFindings?.({ refer_to_mdt: e.target.checked })
          }
        />
        <label
          htmlFor="mdt"
          className="text-[10px] text-slate-500 dark:text-gray-400 cursor-pointer"
        >
          {t('footer.referToMdt')}
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] text-slate-500 dark:text-gray-400">
          {t('footer.nextAppt')}
        </span>
        <Input
          placeholder={t('footer.gaRange')}
          defaultValue={
            r.next_appointment_ga_range
              ? String(r.next_appointment_ga_range)
              : ''
          }
          className={`${styles.input} w-full`}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
            onUpdateFindings?.({
              next_appointment_ga_range: e.target.value || null,
            })
          }
        />
      </div>
    </div>
  );
}

export function ReportActions() {
  const { t } = useTranslation('Report');
  const { studyReportFields, onUpdateFindings, reportDetailMode } =
    useReportData();
  const r = studyReportFields;
  const isFullReport = reportDetailMode === 'full';

  return (
    <div className="border-t border-slate-200 dark:border-gray-700 shrink-0">
      <div className="grid grid-cols-3 gap-3 p-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`${styles.label} font-bold`}>
              {t('footer.comments')}
            </span>
            <div className="flex items-center gap-1">
              <Checkbox
                id="show-report"
                defaultChecked={r.show_comments_in_report !== false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onUpdateFindings?.({
                    show_comments_in_report: e.target.checked,
                  })
                }
              />
              <label
                htmlFor="show-report"
                className="text-[10px] text-slate-500 dark:text-gray-400 cursor-pointer"
              >
                {t('footer.display')}
              </label>
            </div>
          </div>
          <Textarea
            placeholder={t('footer.commentsPlaceholder')}
            defaultValue={r.comment ? String(r.comment) : ''}
            className="text-xs border-slate-300 dark:border-gray-600 min-h-16 resize-y"
            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
              onUpdateFindings?.({ comment: e.target.value || null })
            }
          />
        </div>

        <div>
          <span className={`${styles.label} font-bold block mb-1`}>
            {t('footer.conclusion')}
          </span>
          <Textarea
            placeholder={t('footer.conclusionPlaceholder')}
            defaultValue={r.conclusion ? String(r.conclusion) : ''}
            className="text-xs border-slate-300 dark:border-gray-600 min-h-16 resize-y"
            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
              onUpdateFindings?.({ conclusion: e.target.value || null })
            }
          />
        </div>

        <div>
          <span className={`${styles.label} font-bold block mb-1`}>
            {t('footer.managementPlan')}
          </span>
          <Textarea
            placeholder={t('footer.managementPlanPlaceholder')}
            defaultValue={r.management_plan ? String(r.management_plan) : ''}
            className="text-xs border-slate-300 dark:border-gray-600 min-h-16 resize-y"
            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
              onUpdateFindings?.({ management_plan: e.target.value || null })
            }
          />
        </div>
      </div>

      {isFullReport && (
        <div className="border-t border-slate-200 dark:border-gray-700 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <LuClipboardList className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
              {t('footer.visitSynthesis')}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className={`${styles.label} font-bold block mb-1`}>
                {t('footer.synthesisNotes')}
              </span>
              <Textarea
                placeholder={t('footer.synthesisNotesPlaceholder')}
                defaultValue={
                  r.synthesis_notes ? String(r.synthesis_notes) : ''
                }
                className="text-xs border-slate-300 dark:border-gray-600 min-h-16 resize-y"
                onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                  onUpdateFindings?.({
                    synthesis_notes: e.target.value || null,
                  })
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <span className={`${styles.label} font-bold block mb-1`}>
                  {t('footer.specialistReferral')}
                </span>
                <BooleanPill
                  label={t('footer.specialistReferral')}
                  value={r.specialist_referral}
                  onChange={(v) =>
                    onUpdateFindings?.({ specialist_referral: v })
                  }
                />
              </div>
              {r.specialist_referral && (
                <div>
                  <span className={`${styles.label} block mb-1`}>
                    {t('footer.referralReason')}
                  </span>
                  <Textarea
                    placeholder={t('footer.referralReasonPlaceholder')}
                    defaultValue={
                      r.referral_reason ? String(r.referral_reason) : ''
                    }
                    className="text-xs border-slate-300 dark:border-gray-600 min-h-12 resize-y"
                    onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                      onUpdateFindings?.({
                        referral_reason: e.target.value || null,
                      })
                    }
                  />
                </div>
              )}
              <div>
                <span className={`${styles.label} font-bold block mb-1`}>
                  {t('footer.deliveryDecision')}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  <BooleanPill
                    label={t('footer.decisionBirthingHouse')}
                    value={r.decision_birthing_house}
                    onChange={(v) =>
                      onUpdateFindings?.({ decision_birthing_house: v })
                    }
                  />
                  <BooleanPill
                    label={t('footer.decisionHospitalMaternity')}
                    value={r.decision_hospital_maternity}
                    onChange={(v) =>
                      onUpdateFindings?.({ decision_hospital_maternity: v })
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <span className={`${styles.label} font-bold block mb-1`}>
                {t('footer.overallObservations')}
              </span>
              <Textarea
                placeholder={t('footer.overallObservationsPlaceholder')}
                defaultValue={
                  r.overall_observations ? String(r.overall_observations) : ''
                }
                className="text-xs border-slate-300 dark:border-gray-600 min-h-16 resize-y"
                onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                  onUpdateFindings?.({
                    overall_observations: e.target.value || null,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
